import type { PayloadAction } from '@reduxjs/toolkit';
import type { TObjective, TObjectives } from '@hubertai/screening-question-types';
import type { QuestionEditorsState } from './Question.Editor.Slice';

// Objectives editor reducers (the LLM-driven sibling of slot filling on Open questions).
// Mirrors question.slots.reducer — mutates state.byId[questionId].objectives and flags stateModified.
// Discriminator everywhere: a question "has objectives" iff objectives.list.length > 0, so removing the
// last objective clears the key entirely (matches the chat-backend gate).

// Empty id on purpose: it is the storage key on saved answers and the model's handle on the objective,
// so it must be author-chosen and semantic (e.g. 'team_size'). A generated id reads as noise in stored
// coverage verdicts, and forces the author to clear it before typing.
const getEmptyObjective = (): TObjective => ({ id: '', text: '' });

export const questionObjectivesReducer = {
  addObjective: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string }>) => {
    const question = state.byId[action.payload.questionId];
    if (!question) return;
    if (!question.objectives) question.objectives = { list: [] };
    question.objectives.list.push(getEmptyObjective());
    question.stateModified = true;
  },

  removeObjective: (state: QuestionEditorsState, action: PayloadAction<{ questionId: string; index: number }>) => {
    const { questionId, index } = action.payload;
    const question = state.byId[questionId];
    if (!question?.objectives) return;
    question.objectives.list.splice(index, 1);
    if (question.objectives.list.length === 0) question.objectives = undefined;
    question.stateModified = true;
  },

  updateObjective: (
    state: QuestionEditorsState,
    action: PayloadAction<{ questionId: string; index: number; patch: Partial<TObjective> }>,
  ) => {
    const { questionId, index, patch } = action.payload;
    const question = state.byId[questionId];
    if (!question?.objectives) return;
    const objective = question.objectives.list[index];
    if (!objective) return;
    Object.assign(objective, patch);
    question.stateModified = true;
  },

  updateObjectives: (
    state: QuestionEditorsState,
    action: PayloadAction<{
      questionId: string;
      patch: Partial<Pick<TObjectives, 'guidance' | 'max_followups' | 'active'>>;
    }>,
  ) => {
    const { questionId, patch } = action.payload;
    const question = state.byId[questionId];
    if (!question) return;
    if (!question.objectives) question.objectives = { list: [] };
    Object.assign(question.objectives, patch);
    question.stateModified = true;
  },
};
